import { Suspense } from 'react'
import type { RouteObject } from 'react-router-dom'
import { ErrorBoundary } from '@/components/layout/ErrorBoundary'
import { ContentLoader } from '@/components/ui/loading'
import { PermissionRoute } from './PermissionRoute'

// Type for route handle metadata
export interface RouteHandle {
  title?: string
  breadcrumb?: string
}

// Permission gate — either a single permission or an any-of list.
// Mirrors the two PermissionRoute shapes used by protected AppLayout routes.
export type PermissionGate =
  | { permission: string }
  | { anyPermissions: string[] }

// Wrapper for lazy-loaded page components rendered INSIDE AppLayout's content
// area (via <Outlet/>) — content-sized fallback so the header/nav stay put and
// only the content region shows the loader (no full-viewport jump).
function LazyContent({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<ContentLoader />}>{children}</Suspense>
    </ErrorBoundary>
  )
}

interface MakeProtectedRouteOptions {
  path: string
  lazy: React.LazyExoticComponent<React.ComponentType>
  perm: PermissionGate
  handle: RouteHandle
}

/**
 * Build a protected AppLayout-content route: lazy component wrapped in
 * Suspense + ErrorBoundary (LazyContent), behind a PermissionRoute gate, with
 * handle metadata. Captures the repeated protected-route shape so the routes
 * file stays a slim list of definitions.
 */
export function makeProtectedRoute(opts: MakeProtectedRouteOptions): RouteObject {
  const Component = opts.lazy
  return {
    path: opts.path,
    element: (
      <PermissionRoute {...opts.perm}>
        <LazyContent>
          <Component />
        </LazyContent>
      </PermissionRoute>
    ),
    handle: opts.handle satisfies RouteHandle,
  }
}
